import React, { useState } from 'react';
import { 
  MdGavel, MdAssignment, MdCheckCircle, MdWarning,
  MdCancel, MdPayment, MdSecurity, MdAccessTime,
  MdEdit, MdLanguage, MdCopyright, MdContactSupport,
  MdBook, MdPolicy, MdArrowForward, MdMenu, MdClose,
  MdDownload, MdShare, MdLock, MdVisibility,  MdEmail,MdPhone 
} from 'react-icons/md';
import { FaBalanceScale, FaUserShield } from 'react-icons/fa';
import { RiFilePaperLine } from 'react-icons/ri';
import '../style.css';

const TermsConditions = () => {
  const [activeSection, setActiveSection] = useState('introduction');
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const sections = [
    { id: 'introduction', title: 'Introduction', icon: <MdBook /> },
    { id: 'use-of-services', title: 'Use of Services', icon: <MdAssignment /> },
    { id: 'bookings-payments', title: 'Bookings & Payments', icon: <MdPayment /> },
    { id: 'cancellations', title: 'Cancellations', icon: <MdCancel /> },
    { id: 'responsibilities', title: 'User Responsibilities', icon: <FaUserShield /> },
    { id: 'intellectual-property', title: 'Intellectual Property', icon: <MdCopyright /> },
    { id: 'liability', title: 'Limitation of Liability', icon: <FaBalanceScale /> },
    { id: 'changes', title: 'Changes to Terms', icon: <MdEdit /> },
    { id: 'governing-law', title: 'Governing Law', icon: <MdLanguage /> },
    { id: 'contact', title: 'Contact', icon: <MdContactSupport /> }
  ];

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId);
    setShowMobileMenu(false);
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const lastUpdated = new Date().toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  return (
    <div className="terms-container">
      {/* Mobile Navigation */}
      <div className="mobile-nav">
        <button 
          className="nav-toggle-btn"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        >
          {showMobileMenu ? <MdClose /> : <MdMenu />}
          <span>Sections</span>
        </button>
      </div>

      {/* Mobile Menu */}
      {showMobileMenu && (
        <div className="mobile-menu-overlay" onClick={() => setShowMobileMenu(false)}>
          <div className="mobile-menu-panel" onClick={e => e.stopPropagation()}>
            <div className="menu-header">
              <h3><RiFilePaperLine /> Sections</h3>
              <button className="close-menu-btn" onClick={() => setShowMobileMenu(false)}>
                <MdClose />
              </button>
            </div>
            <div className="menu-items">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`menu-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <span className="item-icon">{section.icon}</span>
                  <span>{section.title}</span>
                  <MdArrowForward />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="terms-header">
        <div className="header-content">
          <div className="header-icon">
            <MdGavel className="main-icon" />
            <RiFilePaperLine className="secondary-icon" />
          </div>
          <h1 className="page-title">
            <MdGavel /> Terms & Conditions
          </h1>
          <p className="page-subtitle">
            Please read these terms carefully before using our services. By booking with us, you agree to these terms.
          </p>
          
          <div className="header-meta">
            <div className="meta-item">
              <MdAccessTime />
              <span>Last Updated: {lastUpdated}</span>
            </div>
            <div className="meta-item">
              <MdWarning />
              <span>Legal Document</span>
            </div>
            <div className="meta-item">
              <MdDownload />
              <span>Download PDF</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="terms-content-wrapper">
        {/* Desktop Sidebar */}
        <aside className="terms-sidebar">
          <div className="sidebar-card">
            <div className="sidebar-header">
              <h3><RiFilePaperLine /> Contents</h3>
              <p>Navigate to sections</p>
            </div>
            
            <nav className="sidebar-nav">
              {sections.map((section) => (
                <button
                  key={section.id}
                  className={`sidebar-link ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => scrollToSection(section.id)}
                >
                  <div className="link-icon">
                    {section.icon}
                  </div>
                  <span className="link-text">{section.title}</span>
                  <MdArrowForward className="link-arrow" />
                </button>
              ))}
            </nav>
            
            <div className="sidebar-footer">
              <div className="quick-action">
                <MdDownload />
                <button className="download-btn">Download Terms</button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main Terms Content */}
        <main className="terms-main">
          {/* Introduction */}
          <section id="introduction" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <MdBook />
              </div>
              <h2>Introduction & Acceptance</h2>
            </div>
            
            <div className="section-content">
              <p className="intro-text">
                Welcome to VNS Tour & Travels. These Terms & Conditions govern your use of our services, 
                including tour bookings, website usage, and all related services provided by us.
              </p>
              
              <div className="notice-box">
                <div className="notice-header">
                  <MdWarning />
                  <h4>Important Notice</h4>
                </div>
                <p>
                  By accessing our website or booking our services, you acknowledge that you have read, 
                  understood, and agree to be bound by these Terms & Conditions.
                </p>
              </div>
              
              <div className="key-points">
                <h4><MdCheckCircle /> Key Points</h4>
                <ul className="points-list">
                  <li><MdCheckCircle /> These terms apply to all bookings and services</li>
                  <li><MdCheckCircle /> You must be at least 18 years old to book</li>
                  <li><MdCheckCircle /> All prices are in Indian Rupees (₹)</li>
                  <li><MdCheckCircle /> We reserve the right to modify these terms</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Use of Services */}
          <section id="use-of-services" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <MdAssignment />
              </div>
              <h2>Use of Our Services</h2>
            </div>
            
            <div className="section-content">
              <div className="rules-grid">
                <div className="rule-card">
                  <h4><MdVisibility /> Website Usage</h4>
                  <ul>
                    <li>Use website for legitimate bookings only</li>
                    <li>Do not attempt to bypass security measures</li>
                    <li>Respect intellectual property rights</li>
                    <li>No unauthorized data collection</li>
                  </ul>
                </div>
                
                <div className="rule-card">
                  <h4><MdLock /> Account Responsibility</h4>
                  <ul>
                    <li>Keep login credentials secure</li>
                    <li>Notify us of unauthorized access</li>
                    <li>Provide accurate information</li>
                    <li>Update contact details promptly</li>
                  </ul>
                </div>
              </div>
              
              <div className="prohibited-list">
                <h4><MdCancel /> Prohibited Activities</h4>
                <div className="prohibited-items">
                  <div className="prohibited-item">
                    <MdCancel />
                    <span>Fraudulent bookings or payments</span>
                  </div>
                  <div className="prohibited-item">
                    <MdCancel />
                    <span>Misuse of promotional offers</span>
                  </div>
                  <div className="prohibited-item">
                    <MdCancel />
                    <span>Harassment of staff or other customers</span>
                  </div>
                  <div className="prohibited-item">
                    <MdCancel />
                    <span>Violation of local laws during tours</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Bookings & Payments */}
          <section id="bookings-payments" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <MdPayment />
              </div>
              <h2>Bookings & Payments</h2>
            </div>
            
            <div className="section-content">
              <div className="booking-process">
                <h4><MdCheckCircle /> Booking Process</h4>
                <div className="process-steps">
                  <div className="step">
                    <div className="step-number">1</div>
                    <div className="step-content">
                      <h5>Select Tour & Date</h5>
                      <p>Choose your preferred tour and available dates</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">2</div>
                    <div className="step-content">
                      <h5>Provide Details</h5>
                      <p>Enter accurate personal and travel information</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">3</div>
                    <div className="step-content">
                      <h5>Make Payment</h5>
                      <p>Complete payment through secure gateway</p>
                    </div>
                  </div>
                  <div className="step">
                    <div className="step-number">4</div>
                    <div className="step-content">
                      <h5>Confirmation</h5>
                      <p>Receive booking confirmation via email/SMS</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="payment-info">
                <h4><MdSecurity /> Payment Information</h4>
                <div className="payment-methods">
                  <div className="method">
                    <div className="method-icon">💳</div>
                    <div className="method-details">
                      <h5>Credit/Debit Cards</h5>
                      <p>Visa, MasterCard, RuPay accepted</p>
                    </div>
                  </div>
                  <div className="method">
                    <div className="method-icon">📱</div>
                    <div className="method-details">
                      <h5>UPI Payments</h5>
                      <p>Google Pay, PhonePe, Paytm</p>
                    </div>
                  </div>
                  <div className="method">
                    <div className="method-icon">🏦</div>
                    <div className="method-details">
                      <h5>Net Banking</h5>
                      <p>All major Indian banks supported</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Cancellations */}
          <section id="cancellations" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <MdCancel />
              </div>
              <h2>Cancellations & Refunds</h2>
            </div>
            
            <div className="section-content">
              <div className="cancellation-policy">
                <h4><MdWarning /> Cancellation Policy</h4>
                
                <div className="policy-table">
                  <div className="table-header">
                    <div className="table-cell">Time Before Tour</div>
                    <div className="table-cell">Cancellation Charge</div>
                    <div className="table-cell">Refund Amount</div>
                  </div>
                  
                  <div className="table-row">
                    <div className="table-cell">30+ days</div>
                    <div className="table-cell">10%</div>
                    <div className="table-cell">90% refund</div>
                  </div>
                  
                  <div className="table-row">
                    <div className="table-cell">15-29 days</div>
                    <div className="table-cell">25%</div>
                    <div className="table-cell">75% refund</div>
                  </div>
                  
                  <div className="table-row">
                    <div className="table-cell">7-14 days</div>
                    <div className="table-cell">50%</div>
                    <div className="table-cell">50% refund</div>
                  </div>
                  
                  <div className="table-row">
                    <div className="table-cell">Less than 7 days</div>
                    <div className="table-cell">100%</div>
                    <div className="table-cell">No refund</div>
                  </div>
                </div>
              </div>
              
              <div className="refund-process">
                <h4><MdAccessTime /> Refund Process</h4>
                <div className="refund-steps">
                  <div className="refund-step">
                    <div className="step-icon">📧</div>
                    <div className="step-details">
                      <h5>Request Submission</h5>
                      <p>Submit cancellation request via email/portal</p>
                    </div>
                  </div>
                  <div className="refund-step">
                    <div className="step-icon">✅</div>
                    <div className="step-details">
                      <h5>Verification</h5>
                      <p>We verify request within 24 hours</p>
                    </div>
                  </div>
                  <div className="refund-step">
                    <div className="step-icon">💸</div>
                    <div className="step-details">
                      <h5>Refund Processing</h5>
                      <p>Refund processed in 5-7 business days</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* User Responsibilities */}
          <section id="responsibilities" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <FaUserShield />
              </div>
              <h2>User Responsibilities</h2>
            </div>
            
            <div className="section-content">
              <div className="responsibilities-list">
                <h4><MdCheckCircle /> Your Responsibilities</h4>
                
                <div className="responsibility-items">
                  <div className="responsibility-item">
                    <div className="item-icon">
                      <MdCheckCircle />
                    </div>
                    <div className="item-content">
                      <h5>Accurate Information</h5>
                      <p>Provide correct personal and travel details</p>
                    </div>
                  </div>
                  
                  <div className="responsibility-item">
                    <div className="item-icon">
                      <MdCheckCircle />
                    </div>
                    <div className="item-content">
                      <h5>Timely Arrival</h5>
                      <p>Arrive at pickup points 15 minutes before time</p>
                    </div>
                  </div>
                  
                  <div className="responsibility-item">
                    <div className="item-icon">
                      <MdCheckCircle />
                    </div>
                    <div className="item-content">
                      <h5>Valid Documents</h5>
                      <p>Carry valid ID proof during tours</p>
                    </div>
                  </div>
                  
                  <div className="responsibility-item">
                    <div className="item-icon">
                      <MdCheckCircle />
                    </div>
                    <div className="item-content">
                      <h5>Respect Culture</h5>
                      <p>Respect local customs and traditions</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="safety-guidelines">
                <h4><MdSecurity /> Safety Guidelines</h4>
                <div className="guidelines">
                  <div className="guideline">
                    <MdWarning />
                    <span>Follow guide instructions during tours</span>
                  </div>
                  <div className="guideline">
                    <MdWarning />
                    <span>Keep valuables secure at all times</span>
                  </div>
                  <div className="guideline">
                    <MdWarning />
                    <span>Stay with group in crowded areas</span>
                  </div>
                  <div className="guideline">
                    <MdWarning />
                    <span>Inform guide of any health issues</span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section id="liability" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <FaBalanceScale />
              </div>
              <h2>Limitation of Liability</h2>
            </div>
            
            <div className="section-content">
              <div className="liability-info">
                <h4><MdWarning /> Important Limitations</h4>
                
                <div className="limitation-cards">
                  <div className="limitation-card">
                    <h5>Force Majeure</h5>
                    <p>We are not liable for cancellations due to natural disasters, strikes, or government actions.</p>
                  </div>
                  
                  <div className="limitation-card">
                    <h5>Personal Belongings</h5>
                    <p>We are not responsible for loss or damage to personal belongings during tours.</p>
                  </div>
                  
                  <div className="limitation-card">
                    <h5>Third-Party Services</h5>
                    <p>We are not liable for services provided by third parties (hotels, transport providers).</p>
                  </div>
                </div>
              </div>
              
              <div className="liability-note">
                <div className="note-icon">
                  <MdWarning />
                </div>
                <div className="note-content">
                  <h5>Maximum Liability</h5>
                  <p>
                    Our maximum liability for any claim is limited to the amount paid for the specific booking 
                    giving rise to the claim.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="terms-section">
            <div className="section-header">
              <div className="section-icon">
                <MdContactSupport />
              </div>
              <h2>Contact Information</h2>
            </div>
            
            <div className="section-content">
              <div className="contact-details">
                <h4><MdContactSupport /> Need Help?</h4>
                <p>Contact us for any questions about these Terms & Conditions:</p>
                
                <div className="contact-methods">
                  <div className="contact-method">
                    <MdEmail />
                    <div>
                      <h5>Email</h5>
                      <p>legal@varanasitours.com</p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <MdPhone />
                    <div>
                      <h5>Phone</h5>
                      <p>+91-543-210-9876 (Legal Department)</p>
                    </div>
                  </div>
                  
                  <div className="contact-method">
                    <MdAccessTime />
                    <div>
                      <h5>Business Hours</h5>
                      <p>Monday-Friday: 9:00 AM - 6:00 PM IST</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="acknowledgment">
                <h4><MdCheckCircle /> Acknowledgment</h4>
                <p>
                  By using our services, you acknowledge that you have read, understood, and agree to be bound 
                  by these Terms & Conditions.
                </p>
                <button className="accept-btn">
                  <MdCheckCircle /> I Accept Terms & Conditions
                </button>
              </div>
            </div>
          </section>
        </main>
      </div>

      {/* Footer */}
      <footer className="terms-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <RiFilePaperLine />
            <div>
              <h4>VNS Tour & Travels</h4>
              <p>Terms & Conditions</p>
            </div>
          </div>
          
          <div className="footer-legal">
            <p>
              These Terms & Conditions are effective from {lastUpdated}. We reserve the right to modify 
              these terms at any time. Continued use of our services constitutes acceptance of updated terms.
            </p>
            <div className="legal-tags">
              <span>Legal Document</span>
              <span>Binding Agreement</span>
              <span>Updated Regularly</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default TermsConditions;